import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import { ProjectDashboardSteps } from './project-dashboard-stepper.const';
import { withStatus } from './project-dashboard-stepper.helper';
import { ProjectStageAPIService } from '@modules/projects/shared/services/project-stage-api.service';
import { IProjectStage } from '@modules/projects/shared/interfaces/project-stage.interface';
import { ProjectDashboardStepperSyncService } from '../../services/project-dashboard-stepper-sync.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';

export type ProjectDashboardStepStatus = 'completed' | 'active' | 'upcoming';

export interface ProjectDashboardStepConfig {
  key: ProjectStage;
  label: string;
  icon: string;
}

export interface ProjectDashboardStepItem extends ProjectDashboardStepConfig {
  status: ProjectDashboardStepStatus;
}

@Component({
  selector: 'project-dashboard-stepper',
  standalone: true,
  templateUrl: './project-dashboard-stepper.component.html',
  styleUrls: ['./project-dashboard-stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AngularSvgIconModule]
})
export class ProjectDashboardStepperComponent {
  protected readonly ProjectStage = ProjectStage;
  private readonly projectStagesService = inject(ProjectStageAPIService);
  private readonly stepperSyncService = inject(ProjectDashboardStepperSyncService);
  private readonly destroyRef = inject(DestroyRef);
  private lastLoadedProjectId: number | null = null;
  private readonly activityTasksDoneMap = signal<Partial<Record<ProjectStage, boolean>>>({});

  currentStage = input<string | null>(null);
  activeStep = input<ProjectStage | null>(null);
  projectId = input<number | null>(null);

  steps = computed<ProjectDashboardStepItem[]>(() => withStatus(ProjectDashboardSteps, this.currentStage()));

  stepSelected = output<ProjectStage>();

  protected onStepClick(stepKey: ProjectStage): void {
    this.stepSelected.emit(stepKey);
  }

  protected isStepSelected(stepKey: ProjectStage): boolean {
    return this.activeStep() === stepKey;
  }

  protected isCurrentProjectStage(stepKey: ProjectStage): boolean {
    return this.currentStage() === stepKey;
  }

  protected isStepChecked(stepKey: ProjectStage): boolean {
    return !!this.activityTasksDoneMap()[stepKey];
  }

  constructor() {
    effect(() => {
      const projectId = this.projectId();
      if (!projectId || this.lastLoadedProjectId === projectId) return;

      this.lastLoadedProjectId = projectId;
      this.loadProjectStages(projectId);
    });

    this.stepperSyncService.stagesUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ projectId, stages }) => {
        if (!projectId || projectId !== this.projectId()) return;
        this.setActivityTasksDoneMap(stages || []);
      });
  }

  private loadProjectStages(projectId: number): void {
    this.projectStagesService
      .getProjectStages(projectId)
      .subscribe((stages) => this.setActivityTasksDoneMap(stages || []));
  }

  private setActivityTasksDoneMap(stages: IProjectStage[]): void {
    const map = stages.reduce<Partial<Record<ProjectStage, boolean>>>((acc, item) => {
      acc[item.stage] = !!item.activityTasksDone;
      return acc;
    }, {});
    this.activityTasksDoneMap.set(map);
  }
}
